// src/oura/components/site/SiteTiles.tsx
//
// The four answers at the top of the staff Site screen: Site health, AI
// Readability, speed on a phone, and the crawl's issues. Each tile opens the
// card that explains it.

import type { ReactNode } from "react";
import { ActivityIcon, BotIcon, ClockIcon, SearchCheckIcon } from "lucide-react";
import type { AeoReadabilityComponent } from "@service/aeo";
import type { PageSpeedRun } from "../../audit";
import { clientArea, MERCHANT_V2 } from "../../merchantV2";
import { cn } from "../../lib/cn";
import { Delta, Dot, OfValue, Tile, Tiles } from "@oura/ui";
import { notMeasured } from "./pageSpeed";
import { clientDragPhrase, dayMonth, dragPhrase, fixFirst, scoreBand } from "./siteCopy";

const ABSENT = <span className="text-gray-400">–</span>;

/**
 * The merchant's band words: no "Weak" or "Critical" and no red. A low score
 * is room to grow, and it is ours to grow, so it reads that way.
 */
function calmBand(score: number): { word: string; text: string } {
    if (score >= 75) return { word: "Strong.", text: "text-emerald-700" };
    if (score >= 50) return { word: "Getting there.", text: "text-blue-700" };
    return { word: "Room to grow.", text: "text-amber-700" };
}

/** "Strong." in its band colour, the first thing a score's sub-line says. */
function BandWord({ score }: { score: number }) {
    const band = scoreBand(score);
    return <span className={cn("font-medium", band.text)}>{band.word}.</span>;
}

export function SiteTiles({
    siteHealth,
    siteHealthAbsent,
    previousSiteHealth,
    readabilityScore,
    readabilityComponents,
    mobile,
    desktop,
    errors,
    warnings,
    pagesCrawled,
    crawlUnread,
    crawledAt,
    onOpen,
    onOpenPhone,
    client,
    calm,
    stacked,
    issuesNote,
}: {
    siteHealth: number | null;
    /** Shown in place of the verdict when there is no score. */
    siteHealthAbsent: string;
    previousSiteHealth?: { score: number; at: string; sameMethod: boolean } | null;
    readabilityScore: number | null;
    readabilityComponents: AeoReadabilityComponent[];
    mobile?: PageSpeedRun;
    desktop?: PageSpeedRun;
    /**
     * Page counts of the findings on screen, after out-of-scope pages. Null
     * while the pages in scope are not known (the client's page, before its
     * page-role read lands or after it fails): no count is shown then.
     */
    errors: number | null;
    warnings: number | null;
    /** Said in place of the counts while they are null. */
    issuesNote?: string;
    pagesCrawled: number;
    /** The crawl did not read the site, so there are no counts to show. */
    crawlUnread: boolean;
    crawledAt?: string | null;
    onOpen: (anchor: "issues" | "machine-read" | "core-web-vitals") => void;
    /** The phone run's card. Absent where the page has none (the client's). */
    onOpenPhone?: () => void;
    /** The client's Website page: "check" for "crawl", and Google's speed test named as such. */
    client?: boolean;
    /**
     * The merchant's Website tab in the redesign: three tiles, Site health,
     * AI readability and speed, in calm words. The issue counts are staff
     * reading.
     */
    calm?: boolean;
    /**
     * Calm tiles only: one row up to 1280px, then one column, for a page
     * that sets a card beside them.
     */
    stacked?: boolean;
}) {
    // The redesign says the scan's date once, on the picker at the top right;
    // a merchant's tiles do not repeat it.
    const quiet = Boolean(client) && MERCHANT_V2;
    const crawlDay = quiet ? null : dayMonth(crawledAt);
    const drag = fixFirst(readabilityComponents);

    // No change without the same method on both sides. Site Health is a share
    // over every catalogue check, so a new check moves it with no change to
    // the site; nothing live says which catalogue scored a scan yet, so on
    // live data `previousSiteHealth` is absent and no change is drawn.
    let healthSub: ReactNode = siteHealthAbsent;
    if (siteHealth !== null) {
        const prev = previousSiteHealth;
        healthSub = (
            <>
                <BandWord score={siteHealth} />{" "}
                {prev?.sameMethod === true ? (
                    <Delta value={siteHealth - prev.score} comparable suffix={`since ${dayMonth(prev.at) ?? "the last scan"}`} />
                ) : (
                    crawlDay && `Crawled ${crawlDay}`
                )}
            </>
        );
    }

    let phoneSub: ReactNode = "No phone run captured.";
    if (mobile) {
        // Two measurements in one tile, so the sub-line names which is which:
        // the number is Lighthouse's lab score, the verdict is real visitors.
        const field = mobile.fieldData === "insufficient"
            ? <span className="font-medium text-gray-400">Not enough real-visitor data.</span>
            : (
                <span className={cn("font-medium", mobile.fieldPass ? "text-emerald-700" : "text-rose-700")}>
                    {client
                        ? `${mobile.fieldPass ? "Passes" : "Fails"} Google's speed test.`
                        : `${mobile.fieldPass ? "Passes" : "Fails"} with real visitors.`}
                </span>
            );
        phoneSub = (
            <>
                {field}
                {!quiet && desktop && !notMeasured(desktop.performance) && ` Desktop ${desktop.performance}`}
            </>
        );
    }
    // Both runs side by side for the merchant: phone and desktop are one
    // question ("is my site fast"), and the desktop score was a footnote.
    const speedValue = (run: PageSpeedRun | undefined, name: string) =>
        run && !notMeasured(run.performance) ? (
            <span className="inline-flex items-baseline gap-1.5">
                {run.performance}
                <span className="text-sm font-normal text-gray-500">{name}</span>
            </span>
        ) : null;

    if (calm) {
        const area = clientArea("site");
        const band = readabilityScore === null ? null : calmBand(readabilityScore);
        const passes = mobile && mobile.fieldData !== "insufficient" ? mobile.fieldPass : null;
        const phone = speedValue(mobile, "Phone");
        const computer = speedValue(desktop, "Desktop");
        return (
            <Tiles cols={3} className={stacked ? "xl:auto-rows-fr xl:grid-cols-1" : undefined}>
                <Tile
                    label="Site health"
                    icon={{ icon: ActivityIcon, area }}
                    value={siteHealth === null ? ABSENT : <OfValue n={siteHealth} unit="/ 100" />}
                    sub={siteHealth === null ? siteHealthAbsent : (
                        <>
                            <span className={cn("font-medium", calmBand(siteHealth).text)}>{calmBand(siteHealth).word}</span> How easily Google can reach your pages.
                        </>
                    )}
                    // Not a link: the findings that explain it are staff
                    // reading, so the merchant's page has nothing to open.
                />
                <Tile
                    label="Can AI read your site"
                    icon={{ icon: BotIcon, area }}
                    value={readabilityScore === null ? ABSENT : <OfValue n={readabilityScore} unit="/ 100" />}
                    sub={band ? (
                        <>
                            <span className={cn("font-medium", band.text)}>{band.word}</span> From {readabilityComponents.length} checks on your pages.
                        </>
                    ) : "Not measured on this scan."}
                    onClick={() => onOpen("machine-read")}
                />
                <Tile
                    label="Speed, out of 100"
                    icon={{ icon: ClockIcon, area }}
                    value={phone || computer ? (
                        <span className="inline-flex flex-wrap items-baseline gap-x-4">
                            {phone ?? ABSENT}
                            {computer ?? ABSENT}
                        </span>
                    ) : ABSENT}
                    sub={passes === true
                        ? <span className="font-medium text-emerald-700">Fast enough for real visitors.</span>
                        : passes === false
                            ? "Room to get faster for real visitors."
                            : mobile ? "Not enough real visitors yet to tell." : "Not measured on this scan."}
                />
            </Tiles>
        );
    }

    return (
        <Tiles cols={4}>
            <Tile
                label="Site health"
                icon={{ icon: ActivityIcon, area: client ? clientArea("site") : "site" }}
                value={siteHealth === null ? ABSENT : <OfValue n={siteHealth} unit="/ 100" />}
                sub={healthSub}
                onClick={() => onOpen("issues")}
            />
            <Tile
                label="AI Readability"
                icon={{ icon: BotIcon, area: client ? clientArea("site") : "site" }}
                value={readabilityScore === null ? ABSENT : <OfValue n={readabilityScore} unit="/ 100" />}
                sub={readabilityScore === null ? "Not measured on this scan." : (
                    <>
                        <BandWord score={readabilityScore} />
                        {drag && ` ${client ? clientDragPhrase(drag) : dragPhrase(drag)}`}
                    </>
                )}
                onClick={() => onOpen("machine-read")}
            />
            <Tile
                label={quiet ? "Speed, out of 100" : "Speed, on a phone"}
                icon={{ icon: ClockIcon, area: client ? clientArea("site") : "site" }}
                value={quiet
                    ? (speedValue(mobile, "Phone") || speedValue(desktop, "Desktop")
                        ? (
                            <span className="inline-flex flex-wrap items-baseline gap-x-4">
                                {speedValue(mobile, "Phone") ?? <span className="text-gray-400">–</span>}
                                {speedValue(desktop, "Desktop") ?? <span className="text-gray-400">–</span>}
                            </span>
                        )
                        : ABSENT)
                    : mobile && !notMeasured(mobile.performance) ? <OfValue n={mobile.performance} unit="/ 100" /> : ABSENT}
                sub={phoneSub}
                onClick={onOpenPhone}
            />
            <Tile
                label={crawlDay ? `Issues, ${crawlDay} ${client ? "check" : "crawl"}` : "Issues"}
                icon={{ icon: SearchCheckIcon, area: client ? clientArea("site") : "site" }}
                value={crawlUnread || errors === null || warnings === null ? ABSENT : <OfValue n={errors + warnings} unit={pagesCrawled > 0 ? `on ${pagesCrawled} ${pagesCrawled === 1 ? "page" : "pages"}` : undefined} />}
                sub={crawlUnread ? "The crawl could not read the site." : errors === null || warnings === null ? issuesNote : (
                    <span className="inline-flex flex-wrap items-center gap-x-1.5">
                        <span className="inline-flex items-center gap-1.5"><Dot tone="rose" />{errors} {errors === 1 ? "error" : "errors"}</span>
                        <span aria-hidden className="text-gray-300">·</span>
                        <span className="inline-flex items-center gap-1.5"><Dot tone="amber" />{warnings} {warnings === 1 ? "warning" : "warnings"}</span>
                    </span>
                )}
                onClick={() => onOpen("issues")}
            />
        </Tiles>
    );
}
